import { TFunction } from 'i18next';
const postwendend = require('postwendend');

import {
  ADDITIONAL_ADDONS_BUSINESS,
  ADDITIONAL_ADDONS_PRO,
  PACKAGE_TYPE_SHOP,
  SHOP_PACKAGE_ADDONS,
  PACKAGE_TYPE_SEO,
  SEO_PACKAGE_ADDONS,
  PACKAGE_TYPE_PLATIN,
  PLATIN_PACKAGE_ADDONS,
  PLATINUM_PACKAGE_ADDONS,
  CHECKOUT_START_ENDPOINT,
  PACKAGE_TYPE_FREE,
  PACKAGE_TYPE_BUSINESS,
  PACKAGE_TYPE_PRO,
} from '../../modules/constants';
import { changeViewToDomain } from './router';
import { generalRequestFail, invalidUserIdToken } from './errors';
import {
  handleError,
  mapToValidCheckoutPackageType,
  mapToValidCheckoutBundleType,
  isLoggedIn,
  redirectToLogin,
  canHaveRankingCoach,
  isGermanLanguage,
  isItalianLanguage,
  isSpanishLanguage,
} from '../../modules/helpers';
import { PackageType } from '../reducers/pricing';
import * as blobApi from '../../modules/blobApi';
import { AppState, getLang, getTemplateSubtype, getEasyMarketingTrackId } from '../reducers';
import { Action, AppDispatch, AppThunk, Map } from '../../definitions/types';
import reload from '../../modules/reload';
import * as Actions from './actions-consts';
import { checkoutConfigGenerated, checkoutConfigRequested } from './checkout';
import getPackages, { PackageDescription } from '../../modules/getPackages';

export function preselectPackage(packageType: PackageType): AppThunk {
  return (dispatch: AppDispatch) => {
    if (packageType === PACKAGE_TYPE_FREE) {
      dispatch(setPackage(packageType));
      dispatch(skipPricingView());
      return;
    }

    dispatch(yieldPackageSelection(packageType));
  };
}

export function setPackage(packageType: PackageType): Action<PackageType> {
  return {
    type: Actions.SET_PACKAGE,
    payload: packageType,
    meta: {
      tracked: { action: 'package', label: 'set' },
    },
  };
}

export function setPackages(packages: Map<PackageDescription>): Action<Map<PackageDescription>> {
  return {
    type: Actions.SET_PACKAGES,
    payload: packages,
  };
}

export function setRankingCoachAvailable(rankingCoachAvailable: boolean): Action<boolean> {
  return {
    type: Actions.SET_RANKING_COACH_AVAILABLE,
    payload: rankingCoachAvailable,
  };
}

export function skipPricingView(): Action<void> {
  return {
    type: Actions.SKIP_PRICING_VIEW,
    meta: {
      tracked: { action: 'pricing-view', label: 'skip' },
    },
  };
}

export function yieldPackageSelection(pkg: PackageType): AppThunk {
  return async (dispatch: AppDispatch, getState: () => AppState): Promise<void> => {
    const language = getLang(getState());
    let additionalProducts: number[] = [];
    dispatch(setPackage(pkg));

    if (pkg === PACKAGE_TYPE_FREE) {
      dispatch(changeViewToDomain());
      return;
    }

    if (!(await isLoggedIn())) {
      redirectToLogin(`/pricing?package=${pkg}`, getState());
      return;
    }

    if (pkg === PACKAGE_TYPE_SHOP) {
      additionalProducts = SHOP_PACKAGE_ADDONS;
    }

    if (pkg === PACKAGE_TYPE_SEO) {
      additionalProducts = SEO_PACKAGE_ADDONS;
    }

    if (pkg === PACKAGE_TYPE_PLATIN) {
      additionalProducts = isGermanLanguage(language)
        ? PLATIN_PACKAGE_ADDONS
        : PLATINUM_PACKAGE_ADDONS;
    }
    return dispatch(forwardToCheckout(pkg, additionalProducts));
  };
}

export function getPackagesAction(
  t: TFunction,
  language: string | null,
  country: string
): AppThunk {
  return async (dispatch: AppDispatch): Promise<void> => {
    const rankingCoachAvailable = await canHaveRankingCoach();
    const hasFivePackages =
      !isItalianLanguage(language) && !isSpanishLanguage(language) && rankingCoachAvailable;
    dispatch(setRankingCoachAvailable(rankingCoachAvailable));
    getPackages(t, hasFivePackages, rankingCoachAvailable, language).then(
      (packages: Map<PackageDescription>) => {
        dispatch(setPackages(packages));
      }
    );
  };
}

function forwardToCheckout(
  packageType: PackageType,
  additionalProducts: number[]
): AppThunk {
  return async (dispatch: AppDispatch, getState: () => AppState) => {
    const state = getState();
    const lang = getLang(state);

    if (state.user.idToken === '') {
      dispatch(invalidUserIdToken());
      setTimeout(reload);
      return;
    }

    dispatch(checkoutConfigRequested());

    const checkoutOptions: blobApi.CheckoutConfigBaseOptions = {
      obfuscatedSubtype: getTemplateSubtype(state),
      additionalProducts: additionalProducts.join(','),
      package: mapToValidCheckoutPackageType(packageType),
      bundle: mapToValidCheckoutBundleType(packageType, lang),
    };

    const ref = state.affiliate.ref ? { ref: state.affiliate.ref } : {};
    const easyMarketingTrackId = getEasyMarketingTrackId(state) || '';
    const trs = easyMarketingTrackId ? { easyMarketingTrackId } : {};

    try {
      const response = await blobApi.generateCheckoutConfigWithoutDomain(
        { ...checkoutOptions, ...ref, ...trs },
        lang,
        state.user.idToken
      );

      if (response.status === 'error') {
        handleError(dispatch, response.errors);
        return;
      }

      // tslint:disable-next-line no-any
      const payload: any = { container: response.message };

      if (window.__BB_UPSELLING__) {
        if (packageType === PACKAGE_TYPE_PRO) {
          payload.checkoutConfiguration = btoa(JSON.stringify(ADDITIONAL_ADDONS_PRO));
        }
        if (packageType === PACKAGE_TYPE_BUSINESS) {
          payload.checkoutConfiguration = btoa(JSON.stringify(ADDITIONAL_ADDONS_BUSINESS));
        }
      }
      dispatch(checkoutConfigGenerated(response.message));
      postwendend(CHECKOUT_START_ENDPOINT, payload);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.warn(error);
      }
      dispatch(generalRequestFail());
    }
  };
}
